// deployment_class: customer_env_allowed
/**
 * TopBar — persistent top bar across every authenticated page. Plan 14
 * §Task 6 + Spec 12 §5.1 + §10.2.
 *
 * Layout (Spec 12 §5.1):
 *   ┌────────────────────────────────────────────────────────┐
 *   │  Kathedral.ai     [Portal: ▼]  [Org: ▼]    [👤 User ▼]│
 *   └────────────────────────────────────────────────────────┘
 *
 * Hidden on /auth/login and /auth/logout (no session = no top bar).
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { PortalSwitcher } from './PortalSwitcher';
import { OrgContextSelector } from './OrgContextSelector';
import { UserMenu } from './UserMenu';

const HIDDEN_ROUTES = new Set(['/auth/login', '/auth/logout']);

export function TopBar(): JSX.Element | null {
  const { status } = useAuth();
  const location = useLocation();
  // Mobile: the secondary nav (portal + org switchers) collapses behind a
  // hamburger so the row never overflows; the wordmark and the UserMenu
  // (sign-out — must always be reachable) stay visible at every width.
  const [menuOpen, setMenuOpen] = useState(false);

  if (HIDDEN_ROUTES.has(location.pathname)) return null;

  const showSecondary = status === 'authenticated';

  return (
    <header
      role="banner"
      // Sticky so the app chrome (sign-out, portal switcher, profile) is ALWAYS
      // visible — never scrolls out of view (UI_BUG_1b: on tall pages like
      // /account/*, whose layout is min-h-screen BELOW this header, any scroll
      // carried a non-sticky header off-screen). z-20 keeps it above page content
      // + the portal-switcher dropdown (z-10); bg-paper is opaque.
      className="sticky top-0 z-20 border-b border-rule bg-paper"
    >
      <div className="px-page py-3 flex items-center justify-between gap-2">
        <Link
          to="/"
          aria-label="Kathedral.ai home"
          className="kth-wordmark-row hover:no-underline shrink-0"
        >
          <img
            src="/kathedral-logo.png"
            alt=""
            aria-hidden="true"
            className="kth-wordmark-icon h-7 w-7"
          />
          <span aria-hidden="true" className="kth-wordmark text-display-3">
            <span className="kth-wordmark-mark">Kathedral</span>
            <span className="kth-wordmark-tld">.ai</span>
          </span>
        </Link>
        <nav className="flex items-center gap-3" aria-label="Main">
          {/* Secondary nav — inline from md up; in the mobile panel below otherwise. */}
          <div className="hidden md:flex items-center gap-3">
            {/* The "Project" menu was removed 2026-08-29 (E3: *"remove the
                project's drop down because we don't need that anymore"*).
                `CreateProjectMenu.tsx` is untouched and still compiles — only
                its two mount points here are gone, so restoring it is one line
                if the decision changes.

                Consequence, recorded rather than discovered later: this was the
                only entry point for starting a project (`POST /v3/projects`).
                Until the File menu of UI-B1 exists, there is no in-app way to
                create one. */}
            <PortalSwitcher />
            {showSecondary && <OrgContextSelector />}
          </div>
          <UserMenu />
          {status === 'unauthenticated' && (
            <Link
              to="/auth/login"
              className="px-3 py-1 border border-rule bg-paper-soft hover:bg-paper-amber text-body-s"
            >
              Sign in
            </Link>
          )}
          {/* Hamburger — mobile only; toggles the secondary nav. */}
          {showSecondary && (
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center h-9 w-9 border border-rule rounded bg-paper-soft text-body-s"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span aria-hidden="true">☰</span>
            </button>
          )}
        </nav>
      </div>
      {/* Mobile secondary-nav panel. */}
      {showSecondary && menuOpen && (
        <div className="md:hidden border-t border-rule px-page py-3 flex flex-col gap-3">
          <PortalSwitcher />
          <OrgContextSelector />
        </div>
      )}
    </header>
  );
}
